// temp-chats.svelte.ts — temporary chats (SAY-1820).
//
// A temporary chat is an ordinary chat on the user's container that this
// browser has flagged as throwaway: it is hidden from the sidebar / search
// and is deleted for good (history + worktree, via DELETE /sessions/:id) the
// moment the user leaves it — navigating elsewhere, closing its tab, or
// closing the window. The flag lives in localStorage so a chat that was left
// open when the browser died is swept on the next launch instead of leaking
// into the sidebar.

import { browser } from '$app/environment';
import { deleteSession } from './api';
import { removeSession } from './sessions-store.svelte';

const STORAGE_KEY = 'sayano.tempChats';

const tempIds = $state<string[]>([]);
// "The next chat I start is temporary" — the strikethrough-bubble toggle on
// the new-chat screen.
let armed = $state(false);

function persist(): void {
	if (!browser) return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(tempIds));
	} catch {
		/* storage full / private mode — the flag is then tab-local */
	}
}

export function loadTempChats(): void {
	if (!browser) return;
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const arr = raw ? (JSON.parse(raw) as unknown) : [];
		tempIds.length = 0;
		if (Array.isArray(arr)) tempIds.push(...arr.filter((x): x is string => typeof x === 'string'));
	} catch {
		tempIds.length = 0;
	}
}

export function isTemporary(sessionId: string | null | undefined): boolean {
	return !!sessionId && tempIds.includes(sessionId);
}

export function temporaryIds(): string[] {
	return tempIds;
}

export function isTempArmed(): boolean {
	return armed;
}

export function setTempArmed(value: boolean): void {
	armed = value;
}

export function markTemporary(sessionId: string): void {
	if (!tempIds.includes(sessionId)) tempIds.push(sessionId);
	persist();
}

function forget(sessionId: string): void {
	const i = tempIds.indexOf(sessionId);
	if (i >= 0) tempIds.splice(i, 1);
	persist();
}

/** Keep a temporary chat as a normal one (the user changed their mind). */
export function keepTemporary(sessionId: string): void {
	forget(sessionId);
}

/** Delete a temporary chat for good. Safe to call twice. */
export async function discardTemporary(sessionId: string): Promise<void> {
	if (!isTemporary(sessionId)) return;
	forget(sessionId);
	removeSession(sessionId);
	try {
		await deleteSession(sessionId);
	} catch (err) {
		// A 404 means it is already gone (swept by another tab); anything else
		// is logged — the id is no longer flagged, so the next sweep won't retry,
		// but the sidebar refresh will show it and the user can trash it.
		if (!(err instanceof Error && /\b404\b/.test(err.message))) console.warn('[temp-chats] discard failed', err);
	}
}

/** Best-effort delete on window close: fetch keepalive survives unload. */
export function discardTemporaryOnUnload(sessionId: string, url: string, headers: Record<string, string>): void {
	if (!isTemporary(sessionId)) return;
	forget(sessionId);
	try {
		void fetch(url, { method: 'DELETE', headers, keepalive: true });
	} catch {
		/* the sweep on next launch catches it */
	}
}

/** Delete every flagged chat that is NOT currently open (startup sweep). */
export async function sweepTemporaryChats(keepOpen: string | null): Promise<void> {
	for (const id of [...tempIds]) {
		if (id === keepOpen) continue;
		await discardTemporary(id);
	}
}
